import { sanitizeRichText } from './sanitize';

/**
 * AI Content Validation Utilities
 *
 * Provides additional validation and sanitization for AI-generated content.
 * AI content should be treated as untrusted and sanitized before rendering.
 *
 * @module utils/aiContentValidator
 */

/**
 * Validates and sanitizes AI-generated content
 *
 * Applies additional security measures for AI-generated content which
 * may contain unexpected or malicious patterns from prompt injection.
 *
 * @param {string} aiContent - Content from AI service
 * @returns {string} - Sanitized content safe for rendering
 *
 * @example
 * const safeContent = validateAIContent(aiGeneratedText);
 * element.innerHTML = safeContent;
 */
export const validateAIContent = (aiContent) => {
  if (!aiContent || typeof aiContent !== 'string') {
    return '';
  }

  // Additional validation for AI content
  // Remove any potential prompt injection artifacts
  let cleaned = aiContent
    // Remove script tags (defense in depth)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    // Remove javascript: URLs
    .replace(/javascript:/gi, '')
    // Remove data: URLs (can contain embedded scripts)
    .replace(/data:text\/html/gi, '')
    // Remove vbscript: URLs
    .replace(/vbscript:/gi, '');

  // Sanitize with DOMPurify
  return sanitizeRichText(cleaned);
};

/**
 * Validates AI-generated text for suspicious patterns
 *
 * Checks for common prompt injection attempts before processing.
 * Returns null if content appears suspicious.
 *
 * @param {string} aiContent - Content to validate
 * @returns {object} - Validation result { isValid: boolean, reason: string }
 *
 * @example
 * const validation = validateAITextSafety(aiText);
 * if (!validation.isValid) {
 *   console.warn('Suspicious AI content:', validation.reason);
 * }
 */
export const validateAITextSafety = (aiContent) => {
  if (!aiContent || typeof aiContent !== 'string') {
    return { isValid: true, reason: null };
  }

  // Check for suspicious patterns that might indicate prompt injection
  const suspiciousPatterns = [
    // Script injection attempts
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onclick=/i,
    /onload=/i,

    // Data URLs with scripts
    /data:text\/html.*<script/i,

    // Iframe injection
    /<iframe/i,

    // Common XSS vectors
    /alert\s*\(/i,
    /document\.cookie/i,
    /document\.write/i,

    // Potential command injection
    /eval\s*\(/i,
    /Function\s*\(/i,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(aiContent)) {
      return {
        isValid: false,
        reason: `Suspicious pattern detected: ${pattern.toString()}`
      };
    }
  }

  return { isValid: true, reason: null };
};

/**
 * Sanitizes and formats AI-generated markdown or plain text
 *
 * Converts line breaks and basic formatting while ensuring safety.
 *
 * @param {string} aiText - AI-generated text
 * @param {object} options - Formatting options
 * @returns {string} - Sanitized HTML
 *
 * @example
 * const html = formatAIText(aiText, { preserveLineBreaks: true });
 */
export const formatAIText = (aiText, options = {}) => {
  if (!aiText || typeof aiText !== 'string') {
    return '';
  }

  const {
    preserveLineBreaks = true,
    convertMarkdown = false,
    maxLength = null,
  } = options;

  let formatted = aiText;

  // Truncate if needed
  if (maxLength && formatted.length > maxLength) {
    formatted = formatted.substring(0, maxLength) + '...';
  }

  // Convert line breaks to <br> tags
  if (preserveLineBreaks) {
    formatted = formatted.replace(/\n/g, '<br>');
  }

  // Basic markdown conversion (if enabled)
  if (convertMarkdown) {
    formatted = formatted
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')  // Bold
      .replace(/\*(.+?)\*/g, '<em>$1</em>')              // Italic
      .replace(/`(.+?)`/g, '<code>$1</code>');           // Code
  }

  // Sanitize before returning
  return validateAIContent(formatted);
};

/**
 * Logs AI content security events for monitoring
 *
 * @param {string} eventType - Type of security event
 * @param {object} details - Event details
 */
export const logAISecurityEvent = (eventType, details) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('[AI Security Event]', eventType, details);
  }

  // In production, you might want to send this to your monitoring service
  // Example:
  // sendToMonitoring({
  //   type: 'ai_security_event',
  //   eventType,
  //   details,
  //   timestamp: new Date().toISOString()
  // });
};

/**
 * Safely enhances text with AI while maintaining security
 *
 * Wrapper function for AI text enhancement that includes validation.
 *
 * @param {string} originalText - Original text to enhance
 * @param {function} enhanceFunction - AI enhancement function
 * @returns {Promise<string>} - Enhanced and sanitized text
 *
 * @example
 * const enhanced = await safeAIEnhancement(text, async (text) => {
 *   return await callAIService(text);
 * });
 */
export const safeAIEnhancement = async (originalText, enhanceFunction) => {
  try {
    // Call AI service
    const enhancedText = await enhanceFunction(originalText);

    // Validate safety
    const validation = validateAITextSafety(enhancedText);

    if (!validation.isValid) {
      logAISecurityEvent('suspicious_ai_content', {
        reason: validation.reason,
        originalLength: originalText.length,
        enhancedLength: enhancedText.length,
      });

      // Return sanitized version with warning
      console.warn('AI content failed safety check, applying strict sanitization');
      return validateAIContent(enhancedText);
    }

    // Sanitize and return
    return validateAIContent(enhancedText);

  } catch (error) {
    console.error('Error in AI enhancement:', error);
    logAISecurityEvent('ai_enhancement_error', {
      error: error.message,
    });

    // Fail securely - return sanitized original text
    return sanitizeRichText(originalText);
  }
};

// Default export for convenience
const aiValidatorUtils = {
  validateAIContent,
  validateAITextSafety,
  formatAIText,
  logAISecurityEvent,
  safeAIEnhancement,
};

export default aiValidatorUtils;
